import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { PATHS } from "../routes/paths";
import { APP_FOOTER_LOGO_URL } from "../config/branding";
import "./VerifyCodePage.css";

function VerifyCodePage() {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const [resending, setResending] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [resendCount, setResendCount] = useState(0);
  const [waitMsg, setWaitMsg] = useState(
    "Please wait 60 seconds before requesting another code.",
  );
  const timerRef = useRef(null);
  const codeInputRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const roleMap = {
    R1: PATHS.OWNER_DASHBOARD,
    R2: PATHS.ADMIN_DASHBOARD,
    R3: "/dashboard/professional", // Shop service professionals
    R4: "/dashboard/freelancer", // Independent freelancer
    R5: PATHS.CUSTOMER_HOME,
    R6: PATHS.PORTAL_ADMIN_DASHBOARD,
    // String-based roles
    professional: "/dashboard/professional", // Shop staff
    freelancer: "/dashboard/freelancer", // Independent freelancer
    customer: PATHS.CUSTOMER_HOME,
    owner: PATHS.OWNER_DASHBOARD,
    admin: PATHS.PORTAL_ADMIN_DASHBOARD,
    portal_admin: PATHS.PORTAL_ADMIN_DASHBOARD, // Portal admin role
  };

  const params = new URLSearchParams(location.search);
  const email = params.get("email") || "";
  const next = params.get("next") || ""; // optional post-verify target (e.g., /choose-role)
  const redirect = params.get("redirect") || ""; // custom redirect path (e.g., /freelancer/profile)

  useEffect(() => {
    setResendDisabled(true);
    setWaitMsg("Please wait 60 seconds before requesting another code.");
    timerRef.current = setTimeout(() => {
      setResendDisabled(false);
      setWaitMsg("");
    }, 60000);
    return () => clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    codeInputRef.current?.focus();
  }, []);

  function decodeJwtPayload(token) {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) return null;
      const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const json = atob(base64);
      return JSON.parse(
        decodeURIComponent(
          [...json]
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join(""),
        ),
      );
    } catch {
      return null;
    }
  }

  function getCodedRole() {
    const storedRole =
      localStorage.getItem("ogestyle_role") ||
      localStorage.getItem("ogestyle_role_redirect");
    if (storedRole) return storedRole;
    const token = localStorage.getItem("ogestyle_token");
    if (token) {
      const payload = decodeJwtPayload(token);
      if (payload && payload.role) return payload.role;
    }
    return "R5";
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const response = await axios.post(`${baseURL}/api/auth/verify-code`, {
        email,
        code,
      });

      if (response.data.status === "success") {
        const { token, expiresAt, user } = response.data;

        if (!token) {
          setError("Verification succeeded but no session token was returned.");
          return;
        }

        localStorage.setItem("ogestyle_token", token);
        if (expiresAt) {
          localStorage.setItem("ogestyle_token_expires", expiresAt);
        }

        if (user?.role) {
          localStorage.setItem("ogestyle_role", user.role);
        }
        if (Array.isArray(user?.roles)) {
          localStorage.setItem("ogestyle_roles", JSON.stringify(user.roles));
        }
        if (user?.hasCompletedSetup !== undefined) {
          localStorage.setItem(
            "ogestyle_hasCompletedSetup",
            user.hasCompletedSetup !== false ? "true" : "false",
          );
        }

        const pending = sessionStorage.getItem("ogestyle_pending_login");
        if (pending) {
          try {
            const parsed = JSON.parse(pending);
            if (parsed?.rememberMe) {
              localStorage.setItem("ogestyle_remember_me", "true");
            } else {
              localStorage.removeItem("ogestyle_remember_me");
            }
          } catch (_err) {
            // Ignore parse errors; do not block login completion.
          }
        }
        sessionStorage.removeItem("ogestyle_pending_login");

        // Check if there's a stored redirect URL from ProtectedRoute
        const storedRedirect = sessionStorage.getItem("redirectAfterLogin");

        if (storedRedirect) {
          // Clear the stored redirect
          sessionStorage.removeItem("redirectAfterLogin");
          navigate(storedRedirect);
        } else if (next) {
          navigate(next); // e.g., /choose-role for multi-role users
        } else if (redirect) {
          navigate(redirect); // custom redirect (e.g., /freelancer/profile)
        } else {
          const codedRole = getCodedRole();
          const redirectPath = roleMap[codedRole] || "/";
          navigate(redirectPath);
        }
      } else {
        setError(response.data.message || "Verification failed.");
      }
    } catch (err) {
      setError("Verification failed. Please try again.");
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMsg("");
    setError("");
    setWaitMsg("");

    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const response = await axios.post(`${baseURL}/api/auth/login`, {
        email,
        password: "",
        resendCode: true,
      });

      if (response.data.status === "success") {
        const expiryMsg =
          response.data.message ||
          "A new verification code has been sent to your email.";
        setResendMsg(expiryMsg);
        setResendCount((prev) => prev + 1);
        setResendDisabled(true);
        setWaitMsg("Please wait 60 seconds before requesting another code.");
        timerRef.current = setTimeout(() => {
          setResendDisabled(false);
          setWaitMsg("");
        }, 60000);
      } else {
        const msg =
          response.data.message || "Failed to resend code. Please try again.";
        setError(msg);
        if (
          msg &&
          (msg.toLowerCase().includes("maximum") ||
            msg.toLowerCase().includes("wait"))
        ) {
          setResendDisabled(true);
          setWaitMsg(response.data.message);
        }
      }
    } catch (err) {
      setError("Failed to resend code. Please try again.");
    }
    setResending(false);
  };

  const maxResends = resendCount >= 3;

  return (
    <div className="verify-code-container">
      {/* Left side - Platform Information */}
      <div className="verify-left-panel">
        <div className="brand-section">
          <div className="brand-logo">
            <img
              src={APP_FOOTER_LOGO_URL}
              alt="OGESTYLE"
              style={{ maxWidth: "200px", height: "auto" }}
              onError={(e) => {
                // Fallback to text if image fails
                e.target.outerHTML = '<div class="brand-logo-text">OS</div>';
              }}
            />
          </div>
          <h1 className="brand-name" style={{ display: "none" }}>
            OGESTYLE
          </h1>
          <p className="brand-tagline">
            The all-in-one platform for beauty professionals
            <br />
            and their clients
          </p>
        </div>

        <div className="features-section">
          <div className="feature-item">
            <div className="feature-icon">📅</div>
            <div className="feature-text">
              <h3>Easy online booking & scheduling</h3>
              <p>Let clients book appointments 24/7 with automated reminders</p>
            </div>
          </div>

          <div className="feature-item">
            <div className="feature-icon">💳</div>
            <div className="feature-text">
              <h3>Secure payments & instant payouts</h3>
              <p>
                Accept payments online and get paid instantly after services
              </p>
            </div>
          </div>

          <div className="feature-item">
            <div className="feature-icon">✨</div>
            <div className="feature-text">
              <h3>Grow your beauty business</h3>
              <p>Reach more clients with our marketing tools and analytics</p>
            </div>
          </div>

          <div className="feature-item">
            <div className="feature-icon">🎯</div>
            <div className="feature-text">
              <h3>Professional dashboard</h3>
              <p>Manage your services, staff, and earnings all in one place</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Verification Form */}
      <div className="verify-right-panel">
        <div className="verify-form-container">
          <h2 className="verify-title">Verify Your Identity</h2>
          <p className="verify-subtitle">Check Your Email</p>
          <p className="verify-description">
            A verification code was sent to:
            <br />
            <strong>{email}</strong>
          </p>
          <p className="verify-hint">
            Can't find your code? Be sure to check your spam/junk folder.
          </p>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Verification Code</label>
              <input
                ref={codeInputRef}
                type="text"
                className="form-input"
                placeholder="Enter 6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="one-time-code"
                maxLength={6}
                required
              />
              <p className="code-expiry">Your code expires in 10 minutes</p>
            </div>

            <button type="submit" className="verify-button">
              Verify Code
            </button>
          </form>

          <div className="resend-section">
            <button
              type="button"
              onClick={handleResend}
              disabled={resending || resendDisabled || maxResends}
              className="resend-link-button"
            >
              {resending ? "Sending..." : "Didn't receive a code? Resend"}
            </button>
          </div>

          {waitMsg && <div className="alert alert-warning">{waitMsg}</div>}
          {resendMsg && <div className="alert alert-success">{resendMsg}</div>}
          {error && <div className="alert alert-error">{error}</div>}
          {maxResends && (
            <div className="alert alert-error">
              You have reached the maximum number of resend attempts.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VerifyCodePage;
